import React, { MutableRefObject, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { MOBILE_AND_TABLET_PORTRAIT } from 'settings/mediaQuery';

import styles from './ProductStickyBar.module.scss';

type Props = React.PropsWithChildren<{
  addButtonRef: MutableRefObject<null | HTMLElement>;
  stickyBarClassName?: string;
  contentClassName?: string;
}>;

function ProductStickyBar({
  children,
  addButtonRef,
  stickyBarClassName,
  contentClassName,
}: Props): JSX.Element {
  const stickyRef = useRef<null | HTMLDivElement>(null);
  const stickyGradiantRef = useRef<null | HTMLDivElement>(null);
  const footerElementPreviousPaddingBottom = useRef<null | string>(null);
  const footerElement: HTMLElement | null = document.querySelector(
    '.page-footer'
  );
  const animate = (
    element: HTMLElement,
    animationClass: string,
    onEnd: (element: HTMLElement) => void
  ) => {
    element.classList.add(animationClass);
    element.addEventListener(
      'animationend',
      function () {
        element.classList.remove(animationClass);
        onEnd(element);
      },
      { once: true }
    );
  };

  const handleScrollSticky = () => {
    const button: HTMLElement | null = addButtonRef.current;
    const sticky: HTMLDivElement | null = stickyRef.current;
    if (!button || !sticky) {
      return;
    }

    const buttonClientRect = button.getBoundingClientRect();
    const buttonPosition = buttonClientRect.top + buttonClientRect.height;
    const containActiveClass = sticky.classList.contains(styles.active);

    if (buttonPosition < 0 && !containActiveClass) {
      animate(sticky, styles['slow-active'], (sticky: HTMLElement) => {
        sticky.classList.add(styles.active);
      });
    } else if (buttonPosition > 0 && containActiveClass) {
      animate(sticky, styles['slow-hide'], (sticky: HTMLElement) => {
        sticky.classList.remove(styles.active);
      });
    }
  };

  const handleResize = () => {
    const sticky: HTMLDivElement | null = stickyRef.current;
    const stickyGradiant: HTMLDivElement | null = stickyGradiantRef.current;
    if (!sticky || !footerElement || !stickyGradiant) {
      return;
    }

    const stickyHeight = sticky.offsetHeight - stickyGradiant.offsetHeight;
    const isMatch = window.matchMedia(MOBILE_AND_TABLET_PORTRAIT).matches;

    if (null === footerElementPreviousPaddingBottom.current) {
      footerElementPreviousPaddingBottom.current =
        footerElement.style.paddingBottom;
    }

    footerElement.style.paddingBottom = isMatch
      ? stickyHeight.toString() + 'px'
      : (footerElementPreviousPaddingBottom.current as string);
  };

  useEffect(() => {
    handleScrollSticky();
    window.addEventListener('scroll', handleScrollSticky);

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScrollSticky);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      className={classNames(styles.stickyBar, stickyBarClassName)}
      ref={stickyRef}
    >
      <div className={classNames(styles.content, contentClassName)}>
        {children}
      </div>
      <div className={styles.gradiant} ref={stickyGradiantRef}></div>
    </div>
  );
}

export default ProductStickyBar;
